package org.matthewo.api

import kotlin.jvm.JvmName
import kotlinx.serialization.Serializable
import kotlin.math.roundToInt
import org.matthewo.symbolprocessor.Period.*
import org.matthewo.schema.MetricsMeta
import org.matthewo.schema.games.*
import org.matthewo.math.plus
import org.matthewo.math.convert

@Serializable
data class MetricsFreightFrenzyCRIAPI<N : Number>(
    override val total: N,
    override val npTotal: N,
    override val auto: N,
    override val teleop: N,
    override val endgame: N,
    override val penalty: N,
    override val apBonus: N,
    override val apLevel1: N,
    override val apLevel2: N,
    override val apLevel3: N,
    override val apCoop: N,
    override val apCarousel: N,
    override val apNavigation: N,
    override val tpLevel1: N,
    override val tpLevel2: N,
    override val tpLevel3: N,
    override val tpCoop: N,
    override val tpShared: N,
    override val epDelivered: N,
    override val epAllianceBalance: N,
    override val epCoopBalance: N,
    override val epSharedTip: N,
    override val epCap: N,
    override val epPark: N,
) : MetricsAPI<N>, MetricsFreightFrenzyCRI<N> {
    constructor(team1: MetricsFreightFrenzyCRI<N>, team2: MetricsFreightFrenzyCRI<N>) : this(
        total = team1.total + team2.total,
        npTotal = team1.npTotal + team2.npTotal,
        auto = team1.auto + team2.auto,
        teleop = team1.teleop + team2.teleop,
        endgame = team1.endgame + team2.endgame,
        penalty = team1.penalty + team2.penalty,
        apBonus = team1.apBonus + team2.apBonus,
        apLevel1 = team1.apLevel1 + team2.apLevel1,
        apLevel2 = team1.apLevel2 + team2.apLevel2,
        apLevel3 = team1.apLevel3 + team2.apLevel3,
        apCoop = team1.apCoop + team2.apCoop,
        apCarousel = team1.apCarousel + team2.apCarousel,
        apNavigation = team1.apNavigation + team2.apNavigation,
        tpLevel1 = team1.tpLevel1 + team2.tpLevel1,
        tpLevel2 = team1.tpLevel2 + team2.tpLevel2,
        tpLevel3 = team1.tpLevel3 + team2.tpLevel3,
        tpCoop = team1.tpCoop + team2.tpCoop,
        tpShared = team1.tpShared + team2.tpShared,
        epDelivered = team1.epDelivered + team2.epDelivered,
        epAllianceBalance = team1.epAllianceBalance + team2.epAllianceBalance,
        epCoopBalance = team1.epCoopBalance + team2.epCoopBalance,
        epSharedTip = team1.epSharedTip + team2.epSharedTip,
        epCap = team1.epCap + team2.epCap,
        epPark = team1.epPark + team2.epPark,
    )
    
    companion object {
        inline operator fun <reified N : Number> invoke(team1: MetricsFreightFrenzyCRI<*>, team2: MetricsFreightFrenzyCRI<*>) = MetricsFreightFrenzyCRIAPI(
            total = team1.total.convert<N>() + team2.total.convert<N>(),
            npTotal = team1.npTotal.convert<N>() + team2.npTotal.convert<N>(),
            auto = team1.auto.convert<N>() + team2.auto.convert<N>(),
            teleop = team1.teleop.convert<N>() + team2.teleop.convert<N>(),
            endgame = team1.endgame.convert<N>() + team2.endgame.convert<N>(),
            penalty = team1.penalty.convert<N>() + team2.penalty.convert<N>(),
            apBonus = team1.apBonus.convert<N>() + team2.apBonus.convert<N>(),
            apLevel1 = team1.apLevel1.convert<N>() + team2.apLevel1.convert<N>(),
            apLevel2 = team1.apLevel2.convert<N>() + team2.apLevel2.convert<N>(),
            apLevel3 = team1.apLevel3.convert<N>() + team2.apLevel3.convert<N>(),
            apCoop = team1.apCoop.convert<N>() + team2.apCoop.convert<N>(),
            apCarousel = team1.apCarousel.convert<N>() + team2.apCarousel.convert<N>(),
            apNavigation = team1.apNavigation.convert<N>() + team2.apNavigation.convert<N>(),
            tpLevel1 = team1.tpLevel1.convert<N>() + team2.tpLevel1.convert<N>(),
            tpLevel2 = team1.tpLevel2.convert<N>() + team2.tpLevel2.convert<N>(),
            tpLevel3 = team1.tpLevel3.convert<N>() + team2.tpLevel3.convert<N>(),
            tpCoop = team1.tpCoop.convert<N>() + team2.tpCoop.convert<N>(),
            tpShared = team1.tpShared.convert<N>() + team2.tpShared.convert<N>(),
            epDelivered = team1.epDelivered.convert<N>() + team2.epDelivered.convert<N>(),
            epAllianceBalance = team1.epAllianceBalance.convert<N>() + team2.epAllianceBalance.convert<N>(),
            epCoopBalance = team1.epCoopBalance.convert<N>() + team2.epCoopBalance.convert<N>(),
            epSharedTip = team1.epSharedTip.convert<N>() + team2.epSharedTip.convert<N>(),
            epCap = team1.epCap.convert<N>() + team2.epCap.convert<N>(),
            epPark = team1.epPark.convert<N>() + team2.epPark.convert<N>(),
        )
    }
}

inline fun <reified N : Number> MetricsFreightFrenzyCRI<*>.convert() = serialize { convert<N>() }

fun <N : Number> MetricsFreightFrenzyCRI<N>.serialize() = MetricsFreightFrenzyCRIAPI(
    total = total,
    npTotal = npTotal,
    auto = auto,
    teleop = teleop,
    endgame = endgame,
    penalty = penalty,
    apBonus = apBonus,
    apLevel1 = apLevel1,
    apLevel2 = apLevel2,
    apLevel3 = apLevel3,
    apCoop = apCoop,
    apCarousel = apCarousel,
    apNavigation = apNavigation,
    tpLevel1 = tpLevel1,
    tpLevel2 = tpLevel2,
    tpLevel3 = tpLevel3,
    tpCoop = tpCoop,
    tpShared = tpShared,
    epDelivered = epDelivered,
    epAllianceBalance = epAllianceBalance,
    epCoopBalance = epCoopBalance,
    epSharedTip = epSharedTip,
    epCap = epCap,
    epPark = epPark,
)

@JvmName("serializeGeneric")
fun <T : Number, R : Number> MetricsFreightFrenzyCRI<T>.serialize(serializer: T.() -> R) = MetricsFreightFrenzyCRIAPI(
    total = total.serializer(),
    npTotal = npTotal.serializer(),
    auto = auto.serializer(),
    teleop = teleop.serializer(),
    endgame = endgame.serializer(),
    penalty = penalty.serializer(),
    apBonus = apBonus.serializer(),
    apLevel1 = apLevel1.serializer(),
    apLevel2 = apLevel2.serializer(),
    apLevel3 = apLevel3.serializer(),
    apCoop = apCoop.serializer(),
    apCarousel = apCarousel.serializer(),
    apNavigation = apNavigation.serializer(),
    tpLevel1 = tpLevel1.serializer(),
    tpLevel2 = tpLevel2.serializer(),
    tpLevel3 = tpLevel3.serializer(),
    tpCoop = tpCoop.serializer(),
    tpShared = tpShared.serializer(),
    epDelivered = epDelivered.serializer(),
    epAllianceBalance = epAllianceBalance.serializer(),
    epCoopBalance = epCoopBalance.serializer(),
    epSharedTip = epSharedTip.serializer(),
    epCap = epCap.serializer(),
    epPark = epPark.serializer(),
)

@JvmName("serializeDouble")
fun MetricsFreightFrenzyCRI<Double>.serialize() = serialize { roundToInt().toShort() }

@JvmName("serializeNumber")
fun MetricsFreightFrenzyCRI<Number>.serialize() = serialize { toShort() }

fun MetricsFreightFrenzyCRI<*>.stringify() = """
        total: $total
        npTotal: $npTotal
        auto: $auto
        teleop: $teleop
        endgame: $endgame
        penalty: $penalty
        apBonus: $apBonus
        apLevel1: $apLevel1
        apLevel2: $apLevel2
        apLevel3: $apLevel3
        apCoop: $apCoop
        apCarousel: $apCarousel
        apNavigation: $apNavigation
        tpLevel1: $tpLevel1
        tpLevel2: $tpLevel2
        tpLevel3: $tpLevel3
        tpCoop: $tpCoop
        tpShared: $tpShared
        epDelivered: $epDelivered
        epAllianceBalance: $epAllianceBalance
        epCoopBalance: $epCoopBalance
        epSharedTip: $epSharedTip
        epCap: $epCap
        epPark: $epPark
    """.trimIndent()

object MetricsFreightFrenzyCRIMeta : MetricsMeta<MetricsFreightFrenzyCRI<*>> {
    override val names = linkedMapOf(
        MetricsFreightFrenzyCRI<*>::total to "Total",
        MetricsFreightFrenzyCRI<*>::npTotal to "NP Total",
        MetricsFreightFrenzyCRI<*>::auto to "Auto",
        MetricsFreightFrenzyCRI<*>::teleop to "Teleop",
        MetricsFreightFrenzyCRI<*>::endgame to "Endgame",
        MetricsFreightFrenzyCRI<*>::penalty to "Penalty",
        MetricsFreightFrenzyCRI<*>::apBonus to "Bonus",
        MetricsFreightFrenzyCRI<*>::apLevel1 to "Level 1",
        MetricsFreightFrenzyCRI<*>::apLevel2 to "Level 2",
        MetricsFreightFrenzyCRI<*>::apLevel3 to "Level 3",
        MetricsFreightFrenzyCRI<*>::apCoop to "Coop",
        MetricsFreightFrenzyCRI<*>::apCarousel to "Carousel",
        MetricsFreightFrenzyCRI<*>::apNavigation to "Navigation",
        MetricsFreightFrenzyCRI<*>::tpLevel1 to "Level 1",
        MetricsFreightFrenzyCRI<*>::tpLevel2 to "Level 2",
        MetricsFreightFrenzyCRI<*>::tpLevel3 to "Level 3",
        MetricsFreightFrenzyCRI<*>::tpCoop to "Coop",
        MetricsFreightFrenzyCRI<*>::tpShared to "Shared",
        MetricsFreightFrenzyCRI<*>::epDelivered to "Duck Deliver",
        MetricsFreightFrenzyCRI<*>::epAllianceBalance to "Alliance Balance",
        MetricsFreightFrenzyCRI<*>::epCoopBalance to "Coop Balance",
        MetricsFreightFrenzyCRI<*>::epSharedTip to "Shared Tip",
        MetricsFreightFrenzyCRI<*>::epCap to "Cap",
        MetricsFreightFrenzyCRI<*>::epPark to "Park",
    )
    
    override val periods = linkedMapOf(
        MetricsFreightFrenzyCRI<*>::total to ALL,
        MetricsFreightFrenzyCRI<*>::npTotal to ALL,
        MetricsFreightFrenzyCRI<*>::auto to AUTO,
        MetricsFreightFrenzyCRI<*>::teleop to TELEOP,
        MetricsFreightFrenzyCRI<*>::endgame to ENDGAME,
        MetricsFreightFrenzyCRI<*>::penalty to ALL,
        MetricsFreightFrenzyCRI<*>::apBonus to AUTO,
        MetricsFreightFrenzyCRI<*>::apLevel1 to AUTO,
        MetricsFreightFrenzyCRI<*>::apLevel2 to AUTO,
        MetricsFreightFrenzyCRI<*>::apLevel3 to AUTO,
        MetricsFreightFrenzyCRI<*>::apCoop to AUTO,
        MetricsFreightFrenzyCRI<*>::apCarousel to AUTO,
        MetricsFreightFrenzyCRI<*>::apNavigation to AUTO,
        MetricsFreightFrenzyCRI<*>::tpLevel1 to TELEOP,
        MetricsFreightFrenzyCRI<*>::tpLevel2 to TELEOP,
        MetricsFreightFrenzyCRI<*>::tpLevel3 to TELEOP,
        MetricsFreightFrenzyCRI<*>::tpCoop to TELEOP,
        MetricsFreightFrenzyCRI<*>::tpShared to TELEOP,
        MetricsFreightFrenzyCRI<*>::epDelivered to ENDGAME,
        MetricsFreightFrenzyCRI<*>::epAllianceBalance to ENDGAME,
        MetricsFreightFrenzyCRI<*>::epCoopBalance to ENDGAME,
        MetricsFreightFrenzyCRI<*>::epSharedTip to ENDGAME,
        MetricsFreightFrenzyCRI<*>::epCap to ENDGAME,
        MetricsFreightFrenzyCRI<*>::epPark to ENDGAME,
    )
}
